// web-common/src/auth.js — the auth glue (D1-DESIGN §3). Three modes behind ONE interface:
//
//   initAuth({ firebaseConfig })      — the real thing: Google popup + email/password via the
//                                       Firebase web SDK (lazy-imported so pure modules stay
//                                       dependency-free).
//   initAuth({ devToken })            — the §10 local rig: getToken() returns the token
//                                       verbatim; signIn is one click. NEVER in a production
//                                       build (the deploy scripts do not set VITE_DEV_TOKEN).
//
// The session/principal contract: after ANY sign-in the app calls GET /v1/me and stores
// { user, principal } in its context. THE CLIENT NEVER DECIDES ROLE FROM THE TOKEN — role
// comes from the users row via /v1/me, the same source the server enforces with. The admin
// SPA's role gate is a curtain (UX), not a lock (every admin route re-checks server-side).

export function initAuth({ firebaseConfig = null, devToken = null } = {}) {
  if (devToken) return devAuth(devToken);
  if (!firebaseConfig) throw new Error('initAuth: pass firebaseConfig (VITE_FIREBASE_CONFIG) or devToken (VITE_DEV_TOKEN, local rig only)');
  return firebaseAuth(firebaseConfig);
}

// ---- dev mode (the local rig) ----
function devAuth(devToken) {
  const KEY = 'qs.devSignedIn';
  let signedIn = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(KEY) === '1'; // survive reloads (walkthrough UX)
  const listeners = new Set();
  const notify = () => {
    try { sessionStorage.setItem(KEY, signedIn ? '1' : '0'); } catch { /* storage unavailable — in-memory only */ }
    listeners.forEach((fn) => fn(signedIn ? { uid: 'dev', displayName: 'Dev' } : null));
  };
  return {
    mode: 'dev',
    onChange(fn) { listeners.add(fn); fn(signedIn ? { uid: 'dev', displayName: 'Dev' } : null); return () => listeners.delete(fn); },
    async signInDev() { signedIn = true; notify(); },
    async signInGoogle() { signedIn = true; notify(); },
    async signInEmail() { signedIn = true; notify(); },
    async signInWithCustomToken() { signedIn = true; notify(); }, // OAuth completion (D-AUTH) — dev rig: one click
    async applyActionCode() { /* no verification emails on the dev rig */ },
    async reloadUser() { return true; }, // dev principals are stamped email-verified (the gate never fires locally)
    hasPasswordProvider() { return true; }, // dev bypasses the gate via auth.mode anyway
    async refreshToken() { return signedIn ? devToken : null; },
    async signOut() { signedIn = false; notify(); },
    async getToken() { return signedIn ? devToken : null; },
  };
}

// ---- firebase mode ----
function firebaseAuth(config) {
  let sdk = null; // lazily loaded firebase/auth surface
  let currentUser = null;
  const listeners = new Set();
  async function load() {
    if (sdk) return sdk;
    const { initializeApp, getApps } = await import('firebase/app');
    const authMod = await import('firebase/auth');
    const app = getApps().length ? getApps()[0] : initializeApp(config);
    const auth = authMod.getAuth(app);
    authMod.onAuthStateChanged(auth, (u) => { currentUser = u; listeners.forEach((fn) => fn(u)); });
    sdk = { auth, ...authMod };
    return sdk;
  }
  return {
    mode: 'firebase',
    onChange(fn) {
      listeners.add(fn);
      load().then(() => fn(currentUser)); // fire once the SDK resolves the persisted session
      return () => listeners.delete(fn);
    },
    async signInGoogle() {
      const s = await load();
      await s.signInWithPopup(s.auth, new s.GoogleAuthProvider());
    },
    async signInEmail(email, password, { create = false } = {}) {
      const s = await load();
      if (create) await s.createUserWithEmailAndPassword(s.auth, email, password);
      else await s.signInWithEmailAndPassword(s.auth, email, password);
    },
    // D-AUTH: the OAuth completion pages sign in with the custom token the /v1 callback
    // minted (the reference's google/linkedin-complete pattern).
    async signInWithCustomToken(token) {
      const s = await load();
      await s.signInWithCustomToken(s.auth, token);
    },
    // D-AUTH: /auth/action applies the emailed verification oobCode.
    async applyActionCode(oobCode) {
      const s = await load();
      await s.applyActionCode(s.auth, oobCode);
    },
    // D-AUTH: the Verify-Email gate polls this — a light reload of the Firebase user record
    // (reflects a verification done in ANY tab/device), returning the fresh emailVerified.
    async reloadUser() {
      const s = await load();
      if (!s.auth.currentUser) return false;
      await s.reload(s.auth.currentUser);
      return !!s.auth.currentUser.emailVerified;
    },
    // D-AUTH gate: only email/password accounts get the verify-email wall. OAuth users sign in
    // via a server-minted custom token (no password provider linked) and are trusted as
    // provider-verified — so they are never gated. Fail CLOSED (true) when the user isn't
    // loaded yet, so an unverified account is never briefly ungated.
    hasPasswordProvider() {
      const u = sdk && sdk.auth.currentUser;
      if (!u) return true;
      return (u.providerData || []).some((p) => p && p.providerId === 'password');
    },
    // D-AUTH: after verification, force-refresh so the email_verified claim rides the
    // very next /v1 call (ensureUser stamps the account on first sighting).
    async refreshToken() {
      const s = await load();
      if (!s.auth.currentUser) return null;
      return s.auth.currentUser.getIdToken(true);
    },
    async signOut() { const s = await load(); await s.signOut(s.auth); },
    async getToken() {
      if (!sdk || !sdk.auth.currentUser) return null;
      return sdk.auth.currentUser.getIdToken(); // SDK-cached, auto-refreshed
    },
  };
}
